import React, { createContext, useCallback, useContext, useState } from 'react';
import userService from '../services/userService';
import * as storage from '../services/storage';
import { IUser } from '../shared/interfaces/user-interfaces';

interface AuthState {
  token?: string;
  user?: Partial<IUser>;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user?: Partial<IUser>;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: Partial<IUser>): void;
  fetchUserData(): Promise<IUser>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = storage.getToken();
    const user = storage.getUser();

    if (token && user) {
      return {
        token,
        user,
      };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }: SignInCredentials) => {
    const response = await userService.signIn({
      email,
      password,
    });

    const { token, user } = response.data;

    storage.setToken(token);
    storage.setUser(user);

    setData({ token, user });
  }, []);

  const signOut = useCallback(() => {
    storage.clearToken();
    storage.clearUser();

    setData({});
  }, []);

  const updateUser = useCallback(
    (user: Partial<IUser>) => {
      setData({
        ...data,
        user: {
          ...data.user,
          ...user,
        },
      });
    },
    [data],
  );

  const fetchUserData = useCallback(async () => {
    const response = await userService.retrieve(data.user!.id!);

    storage.setUser(response.data);

    setData({
      ...data,
      user: response.data,
    });

    return response.data;
  }, [data]);

  return (
    <AuthContext.Provider
      value={{ user: data?.user, signIn, signOut, updateUser, fetchUserData }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
